/**
 * @file LiveEmail.jsx
 * @description Live Email component for displaying and managing live email conversations.
 *              Includes filtering, customer details, and email conversation display.
 *
 * @module LiveEmail
 *
 * @requires React, useState, useEffect - React hooks for component state and effects.
 * @requires @mui/material - Material UI components for UI elements.
 * @requires @mui/material/Grid2 - Material UI Grid component for layout.
 * @requires @mui/icons-material/LocalPhone - Material UI icon for phone.
 * @requires ../services/api - API service for fetching live email data.
 *
 * @exports LiveEmail - Main component for displaying live email conversations.
 */
import React, { useState, useEffect } from "react";
import {
  Box, Chip, CircularProgress, Container,
  MenuItem, Select, TextField, Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { getLiveEmail } from "../services/api";

// ── Constants ──────────────────────────────────────────────────────
const BLUE  = "#378ADD";
const PINK  = "#D4537E";
const GREEN = "#1D9E75";
const AMBER = "#BA7517";
const GRAY  = "#888780";

// ── Helpers ────────────────────────────────────────────────────────
function fmtPhone(s = "") {
  return s.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
}

function fmtTime(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Filter config ──────────────────────────────────────────────────
const RESPONSE_OPTIONS = [
  { label: "All",                   value: 1  },
  { label: "At least 1 response",   value: 2  },
  { label: "At least 2 responses",  value: 3  },
  { label: "At least 5 responses",  value: 6  },
  { label: "At least 10 responses", value: 11 },
];

const LAST_RESPONSE_OPTIONS = [
  { label: "Last 1 min",   value: 1       },
  { label: "Last 5 mins",  value: 5       },
  { label: "Last 10 mins", value: 10      },
  { label: "Last 30 mins", value: 30      },
  { label: "Last 2 hrs",   value: 120     },
  { label: "Last 8 hrs",   value: 480     },
  { label: "Last 10 days", value: 14400   },
  { label: "All",          value: 1440000 },
];

// ── PillSelect ─────────────────────────────────────────────────────
function PillSelect({ label, value, onChange, options }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {label}
      </Typography>
      <Select
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        size="small"
        variant="outlined"
        sx={{
          fontSize: 12, borderRadius: "20px", height: 30, minWidth: 180,
          ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
          ".MuiSelect-select": { py: "4px", px: "12px", fontSize: 12, color: "text.secondary" },
          ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
        }}
      >
        {options.map(o => (
          <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12 }}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

// ── Email bubble — renders HTML content safely ─────────────────────
function EmailBubble({ side, html, text, time, badge, isEmpty }) {
  const isAI  = side === "ai";
  const color = isAI ? BLUE : GREEN;
  const content = html || text;

  if (isEmpty && !content) return null;

  return (
    <Box sx={{
      display: "flex",
      flexDirection: isAI ? "row" : "row-reverse",
      gap: 1, mb: 1.5,
    }}>
      {/* Avatar */}
      <Box sx={{
        width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
        background: color, display: "flex", alignItems: "center",
        justifyContent: "center", mt: "2px",
      }}>
        <Typography sx={{ fontSize: 10, color: "#fff", fontWeight: 700 }}>
          {isAI ? "AI" : "U"}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: "88%" }}>
        <Box sx={{
          background: isAI ? "rgba(55,138,221,0.07)" : "rgba(29,158,117,0.07)",
          border: "0.5px solid",
          borderColor: isAI ? "rgba(55,138,221,0.2)" : "rgba(29,158,117,0.2)",
          borderRadius: isAI ? "4px 12px 12px 12px" : "12px 4px 12px 12px",
          px: "12px", py: "8px",
          fontSize: 13, lineHeight: 1.6,
          color: "text.primary",
          // Scope HTML styles inside email bubbles
          "& a":  { color: BLUE, textDecoration: "underline" },
          "& p":  { margin: 0, mb: "4px" },
          "& ul": { mt: 0, mb: "4px", pl: "18px" },
          "& li": { mb: "2px" },
          "& table": { borderCollapse: "collapse", width: "100%", fontSize: 12 },
          "& td, & th": { padding: "4px 8px", border: "0.5px solid #ddd" },
        }}>
          {content
            ? (
              html
                ? <Box dangerouslySetInnerHTML={{ __html: html }} />
                : text
            )
            : (
              <Typography sx={{ fontSize: 12, color: "text.disabled", fontStyle: "italic" }}>
                —
              </Typography>
            )
          }
        </Box>
        <Box sx={{
          display: "flex", alignItems: "center",
          gap: 0.5, mt: 0.5,
          flexDirection: isAI ? "row" : "row-reverse",
        }}>
          {badge && (
            <Chip
              label={badge}
              size="small"
              sx={{
                height: 16, fontSize: 9, fontWeight: 700,
                textTransform: "uppercase",
                background: color, color: "#fff",
                borderRadius: "4px",
              }}
            />
          )}
          <Typography sx={{ fontSize: 10, color: "text.disabled" }}>
            {isAI ? "AI" : "User"} · {fmtTime(time)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Customer card ──────────────────────────────────────────────────
function CustomerCard({ customer }) {
  const [expanded, setExpanded] = useState(true);

  const chatCount = customer.chatData?.length || 0;
  const respCount = customer.responsescount ?? 0;
  const lastMins  = customer.lastresponsemins ?? null;

  const statusColor = lastMins == null ? GRAY
    : lastMins <= 5  ? GREEN
    : lastMins <= 30 ? AMBER
    : PINK;

  const statusLabel = lastMins == null ? "No response"
    : lastMins <= 1  ? "Just now"
    : lastMins <= 60 ? `${lastMins}m ago`
    : `${Math.round(lastMins / 60)}h ago`;

  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, overflow: "hidden",
    }}>
      {/* ── Card header ── */}
      <Box
        onClick={() => setExpanded(e => !e)}
        sx={{
          px: 2, py: "10px",
          background: "#f7f7f7",
          cursor: "pointer",
          borderBottom: expanded ? "0.5px solid" : "none",
          borderColor: "divider",
          "&:hover": { background: "#f0f0f0" },
        }}
      >
        {/* Row 1 — status dot + name + phone + expand */}
        <Box sx={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", mb: 0.5,
        }}>
          <Box sx={{
            display: "flex", alignItems: "center",
            gap: 1, minWidth: 0, overflow: "hidden",
          }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: "50%",
              background: statusColor, flexShrink: 0,
            }} />
            <Typography sx={{
              fontSize: 13, fontWeight: 600, color: "text.primary",
              overflow: "hidden", textOverflow: "ellipsis",
              whiteSpace: "nowrap", flexShrink: 1,
            }}>
              {customer.lastname}, {customer.firstname}
            </Typography>
            <Chip
              icon={<LocalPhoneIcon sx={{ fontSize: "12px !important" }} />}
              label={fmtPhone(customer.customeruniqueid || "")}
              size="small"
              sx={{
                height: 20, fontSize: 11, flexShrink: 0,
                background: "rgba(55,138,221,0.1)", color: BLUE,
                border: "0.5px solid rgba(55,138,221,0.25)",
                "& .MuiChip-icon": { color: BLUE },
              }}
            />
          </Box>
          <Typography sx={{
            fontSize: 14, color: "text.secondary", flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform .2s", lineHeight: 1, ml: 1,
          }}>
            ▾
          </Typography>
        </Box>

        {/* Row 2 — ID chip + status + stats */}
        <Box sx={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", pl: "16px",
        }}>
          <Chip
            label={`CRFN2025${customer.chatData?.[0]?.customerdataid}00`}
            size="small"
            sx={{
              height: 18, fontSize: 10,
              background: "transparent",
              border: "0.5px solid", borderColor: "divider",
              color: "text.secondary",
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 11, color: statusColor, fontWeight: 500 }}>
              {statusLabel}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
              {respCount} resp · {chatCount} msgs
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Email thread body ── */}
      {expanded && (
        <Box sx={{
          height: 520, overflowY: "auto",
          px: 2, py: 1.5,
          background: "#fafafa",
        }}>
          {customer.chatData?.length > 0
            ? customer.chatData.map((chat, idx) => (
                <Box key={idx}>
                  <EmailBubble
                    side="ai"
                    html={chat.cararesponse}
                    time={chat.cararesponsetime}
                    badge={chat.newcara}
                  />
                  <EmailBubble
                    side="user"
                    html={chat.userresponse}
                    time={chat.userresponsetime}
                    badge={chat.newuser}
                    isEmpty={!chat.userresponse}
                  />
                </Box>
              ))
            : (
              <Typography sx={{
                fontSize: 12, color: "text.disabled",
                textAlign: "center", mt: 4,
              }}>
                No emails yet
              </Typography>
            )
          }
        </Box>
      )}
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const LiveEmail = () => {
  const [customers,          setCustomers]          = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [searchTerm,         setSearchTerm]         = useState("");
  const [filterResponses,    setFilterResponses]    = useState(2);
  const [filterLastResponse, setFilterLastResponse] = useState(10);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const data = await getLiveEmail();
        if (active && Array.isArray(data)) {
          setCustomers(data);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching email records:", err);
        if (active) setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, 30000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  const filtered = customers.filter(c => {
    const matchSearch = searchTerm
      ? c.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.lastname?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    const matchResp = (c.responsescount ?? 0) >= filterResponses;
    const matchTime = (c.lastresponsemins ?? Number.MAX_SAFE_INTEGER) <= filterLastResponse;
    return matchSearch && matchResp && matchTime;
  });

  return (
    <Container maxWidth={false} disableGutters>

      {/* ── Filter bar ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 2, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        {/* Search */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1, minWidth: 200 }}>
          <Typography sx={{
            fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Search
          </Typography>
          <TextField
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="First or last name…"
            size="small"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: 12, borderRadius: "20px", height: 30,
                "& fieldset": { borderColor: "divider", borderWidth: "0.5px" },
                "&:hover fieldset": { borderColor: BLUE },
              },
              "& .MuiInputBase-input": { py: "4px", px: "12px" },
            }}
          />
        </Box>

        <PillSelect
          label="Responses"
          value={filterResponses}
          onChange={setFilterResponses}
          options={RESPONSE_OPTIONS}
        />

        <PillSelect
          label="Last response"
          value={filterLastResponse}
          onChange={setFilterLastResponse}
          options={LAST_RESPONSE_OPTIONS}
        />

        {/* Live count badge */}
        <Box sx={{
          alignSelf: "flex-end",
          px: "12px", py: "4px", borderRadius: "20px",
          background: "rgba(55,138,221,0.1)",
          border: "0.5px solid rgba(55,138,221,0.25)",
          fontSize: 12, color: BLUE, fontWeight: 500,
          whiteSpace: "nowrap",
        }}>
          {filtered.length} active {filtered.length === 1 ? "thread" : "threads"}
        </Box>
      </Box>

      {/* ── Loading ── */}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 4 }}>
          <CircularProgress size={20} /> Loading emails…
        </Box>
      )}

      {/* ── Empty ── */}
      {!loading && filtered.length === 0 && (
        <Typography sx={{ p: 4, textAlign: "center", color: "text.secondary", fontSize: 13 }}>
          No active email threads match your filters.
        </Typography>
      )}

      {/* ── Cards ── */}
      {!loading && filtered.length > 0 && (
        <Grid container spacing={2}>
          {filtered.map(customer => (
            <Grid size={{ xs: 12, sm: 6, xl: 4 }} key={customer.customerdataid}>
              <CustomerCard customer={customer} />
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default LiveEmail;